import React from 'react';
import "./smile-pages.css";

export default function HowDoesSmilingMakeYouMoreGrateful() {
  return (
    <div className="container-box mt-5 mb-5 smile-page">

      <h1>How does Smiling Make You More Grateful?</h1>

      <p>
        Smiling can make you feel more <strong>grateful</strong> by influencing your brain,
        emotions, and perception in ways that promote <strong>positive thinking and appreciation</strong>.
        Smiling acts as a kind of emotional “primer” that opens you up to noticing the good around you —
        and here's how that works, backed by science:
      </p>

      <hr />

      {/* 1 */}
      <h3>🥰 1. Smiling Shifts Your Brain <em>Into</em> a Positive State</h3>

      <p>
        When you smile, your brain releases <strong>dopamine, serotonin, and endorphins</strong> —
        all chemicals associated with <strong>happiness and well-being</strong>. These neurochemicals:
      </p>

      <ul>
        <li>Reduce negative emotions like stress or resentment</li>
        <li>Enhance your capacity to <strong>notice and savor positive experiences</strong></li>
        <li>Make it easier to <strong>recognize things to be grateful for</strong></li>
      </ul>

      <p><strong>📚 Source:</strong></p>
      <ul>
        <li>Niedenthal, P. M. (2007). <em>Embodied Emotion: Facial expressions influence emotional experience.</em></li>
      </ul>

      <hr />

      {/* 2 */}
      <h3>🧠 2. Smiling Activates the Same Brain Regions as Gratitude</h3>

      <p>
        Both smiling and practicing gratitude stimulate areas of the brain associated with
        <strong> reward, empathy, and emotional regulation</strong>, such as:
      </p>

      <ul>
        <li><strong>The prefrontal cortex</strong> (positive decision-making)</li>
        <li><strong>The anterior cingulate cortex</strong> (emotional regulation)</li>
        <li><strong>The ventral striatum</strong> (pleasure and reward)</li>
      </ul>

      <p>
        So, when you smile, you’re <strong>engaging the same neural circuitry</strong> that supports
        feelings of appreciation — making gratitude more accessible.
      </p>

      <p><strong>📚 Source:</strong></p>
      <ul>
        <li>
          Fox, G. R. et al. (2015).
          <em> Neural correlates of gratitude.</em> NeuroImage.
        </li>
      </ul>

      <hr />

      {/* 3 */}
      <h3>💡 3. Facial Feedback Loop Reinforces Positive Emotion</h3>

      <p>
        The <strong>Facial Feedback Hypothesis</strong> suggests that
        <strong> smiling doesn’t just reflect how you feel — smiling changes how you feel. </strong>
        When you smile:
      </p>

      <ul>
        <li>Your body sends a signal to your brain that you’re safe and content.</li>
        <li>This encourages your mind to <strong>scan for things to appreciate</strong>.</li>
        <li>You become more likely to notice the <strong>small wins and daily blessings</strong>.</li>
      </ul>

      <p><strong>📚 Study:</strong></p>
      <p>
        Strack, Martin, & Stepper (1988) – People who were tricked into smiling rated
        experiences more positively.
      </p>

      <hr />

      {/* 4 */}
      <h3>🧘 4. Smiling Broadens Your Perspective</h3>

      <p>
        According to the <strong>Broaden-and-Build Theory</strong> by psychologist Barbara Fredrickson:
      </p>

      <ul>
        <li>
          Positive emotions (like those sparked by smiling) <strong>widen your attention </strong>
          and <strong>open your mind</strong> to more possibilities.
        </li>
        <li>
          This helps you see the <strong>bigger picture</strong>, recognize <strong>good moments</strong>,
          and feel grateful more often.
        </li>
      </ul>

      <p><strong>📚 Source:</strong></p>
      <ul>
        <li>
          Fredrickson, B. L. (2001).
          <em> The role of positive emotions in positive psychology.</em>
        </li>
      </ul>

      <hr />

      {/* 5 */}
      <h3>🤝 5. Smiling Enhances Social Gratitude</h3>

      <p>When you smile at others:</p>

      <ul>
        <li>You’re more likely to receive smiles, kindness, and cooperation in return.</li>
        <li>
          These interactions <strong>reinforce feelings of social support</strong>,
          which is a major source of gratitude.
        </li>
        <li>
          Positive social feedback makes you more likely to
          <strong> reflect on what you're thankful for.</strong>
        </li>
      </ul>

      <hr />

      {/* SUMMARY TABLE */}
      <h3>✅ Summary: How Smiling Boosts Gratitude</h3>

      <table className="table table-bordered mt-3">
        <thead>
          <tr>
            <th>Mechanism</th>
            <th>Impact on Gratitude</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Releases mood-boosting chemicals</td>
            <td>Makes you feel more appreciative</td>
          </tr>
          <tr>
            <td>Activates overlapping brain areas</td>
            <td>Strengthens gratitude and emotional awareness</td>
          </tr>
          <tr>
            <td>Facial feedback loop</td>
            <td>Triggers positive emotions and reflection</td>
          </tr>
          <tr>
            <td>Broadened perspective</td>
            <td>Helps you notice things to be thankful for</td>
          </tr>
          <tr>
            <td>Positive social response</td>
            <td>Reinforces emotional warmth and connection</td>
          </tr>
        </tbody>
      </table>

      <hr />

      {/* KEYS TO REMEMBER */}
      <h3>🔑 Keys to Remember</h3>

      <p>
        <b>Smiling helps you become more grateful</b> by shifting your emotional state,
        engaging your brain's reward systems, and encouraging a broader, more positive outlook.
        Smiling primes your mind to <strong>notice and appreciate the good</strong> — both big and small.
      </p>
      <p>
        <b>That Smile App is here to help you express gratitude.</b>
      </p>

    </div>
  );
}
